<template>
    <v-dialog v-model="dialog" max-width="700" persistent>
        <v-card>
            <v-card-title style="background-color: #1a5b0f; display: flex; justify-content: space-between; align-items: center;">
                <span class="font-weight-bold text-h6 text-white">Print All Approved Workloads</span>
                <v-btn :disabled="generating" flat icon color="darkGreen" @click="dialog = false">
                    <v-icon>mdi-close</v-icon>
                </v-btn>
            </v-card-title>
            <v-divider></v-divider>
            <v-card-text class="mt-3">
                <v-row dense>
                    <v-col cols="12">
                        <v-select
                            v-model="programFilter"
                            :items="programOptions"
                            label="Filter by Program/Department"
                            variant="outlined"
                            hide-details
                            density="comfortable"
                            rounded="lg"
                            color="primary"
                        ></v-select>
                    </v-col>
                    <v-col cols="12" class="mt-3">
                        <v-data-table
                            :headers="headers"
                            :items="filteredApproved"
                            :items-per-page="10"
                            :items-per-page-options="[10, 25, 50, -1]"
                            density="compact"
                            class="text-caption"
                        >
                            <template v-slot:no-data>
                                <span class="text-grey">No approved workloads found.</span>
                            </template>
                        </v-data-table>
                    </v-col>
                </v-row>
                <v-alert v-if="errorMsg" type="error" class="mt-3" density="compact">
                    <div class="font-weight-bold">{{ errorMsg }}</div>
                    <div v-for="detail in errorDetails" :key="detail" class="mt-1">
                        {{ detail }}
                    </div>
                </v-alert>
            </v-card-text>
            <v-divider></v-divider>
            <v-card-actions class="pa-4">
                <v-spacer></v-spacer>
                <v-btn
                    :disabled="filteredApproved.length === 0 || generating"
                    :loading="generating"
                    @click="printAll"
                    color="green"
                    variant="elevated"
                    prepend-icon="mdi-printer"
                >
                    Print ({{ filteredApproved.length }})
                </v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script>
import axios from 'axios';
import { defineComponent } from 'vue';
import { exportAllApprovedPDF } from '@/composables/pdf/print-all-approved';

export default defineComponent({
    data() {
        return {
            dialog: false,
            generating: false,
            errorMsg: null,
            errorDetails: [],
            programFilter: 'All',
            programOptions: [],
            approvedData: [],
            semesterSchoolyearId: null,
            headers: [
                { title: 'Faculty Name', key: 'faculty_name', sortable: true },
                { title: 'Program/Department', key: 'department', sortable: true },
            ],
        };
    },
    computed: {
        filteredApproved() {
            if (this.programFilter === 'All') return this.approvedData;
            return this.approvedData.filter(item => (item.department || 'Unknown Program') === this.programFilter);
        },
    },
    methods: {
        init(semesterSchoolyearId, approvedData, programOptions) {
            this.dialog = true;
            this.generating = false;
            this.errorMsg = null;
            this.errorDetails = [];
            this.semesterSchoolyearId = semesterSchoolyearId;
            this.approvedData = approvedData;
            this.programOptions = programOptions;
            this.programFilter = 'All';
        },
        getPersonName(person) {
            if (!person) return 'Unknown user';
            const middleInitial = person.mname ? ` ${person.mname[0]}.` : '';
            const suffix = person.suffix ? ` ${person.suffix}` : '';
            return `${person.fname ?? ''}${middleInitial} ${person.lname ?? ''}${suffix}`.trim();
        },
        hasValidBase64Image(base64Value) {
            if (typeof base64Value !== 'string') return false;
            const normalized = base64Value.replace(/\s/g, '');
            if (normalized.length < 40) return false;
            return /^[A-Za-z0-9+/=]+$/.test(normalized);
        },
        validateWorkloadImages(workload) {
            const issues = [];
            const facultyName = workload.faculty_name || 'Unknown faculty';

            const validatePeople = (people, roleLabel) => {
                for (const person of people) {
                    if (!person?.esign?.b64_name) {
                        issues.push(`${facultyName}: ${roleLabel} ${this.getPersonName(person)} has no e-signature.`);
                    } else if (!this.hasValidBase64Image(person.esign.b64_name)) {
                        issues.push(`${facultyName}: ${roleLabel} ${this.getPersonName(person)} has an invalid e-signature image.`);
                    }
                }
            };

            validatePeople(workload.preparer || [], 'Preparer');
            validatePeople(workload.recommenders || [], 'Recommender');

            if (!workload.pre_approver?.esign?.b64_name) {
                issues.push(`${facultyName}: Pre-approver ${this.getPersonName(workload.pre_approver)} has no e-signature.`);
            } else if (!this.hasValidBase64Image(workload.pre_approver.esign.b64_name)) {
                issues.push(`${facultyName}: Pre-approver ${this.getPersonName(workload.pre_approver)} has an invalid e-signature image.`);
            }

            if (!workload.approver?.esign?.b64_name) {
                issues.push(`${facultyName}: Approver ${this.getPersonName(workload.approver)} has no e-signature.`);
            } else if (!this.hasValidBase64Image(workload.approver.esign.b64_name)) {
                issues.push(`${facultyName}: Approver ${this.getPersonName(workload.approver)} has an invalid e-signature image.`);
            }

            return issues;
        },
        async printAll() {
            this.generating = true;
            this.errorMsg = null;
            this.errorDetails = [];
            const token = localStorage.getItem('aums-token');
            const allWorkloadsData = [];

            try {
                const validationIssues = this.filteredApproved.flatMap(workload => this.validateWorkloadImages(workload));

                if (validationIssues.length > 0) {
                    this.errorMsg = 'Cannot generate PDF because some e-signature images are missing or invalid.';
                    this.errorDetails = validationIssues;
                    return;
                }

                for (const workload of this.filteredApproved) {
                    const [courseRes, adminRes, repRes] = await Promise.all([
                        axios.get(
                            `${process.env.VUE_APP_SERVER_URL}/faculty-load/findAllBySemesterAndSchoolyear/${workload.user_id}/${this.semesterSchoolyearId}`,
                            { headers: { Authorization: `Bearer ${token}` } }
                        ),
                        axios.get(
                            `${process.env.VUE_APP_SERVER_URL}/admin-load/findAllLoadByUserAndSemester/${workload.user_id}/${this.semesterSchoolyearId}`,
                            { headers: { Authorization: `Bearer ${token}` } }
                        ),
                        axios.get(
                            `${process.env.VUE_APP_SERVER_URL}/rep-unit-load/findAllLoadByUserAndSemester/${workload.user_id}/${this.semesterSchoolyearId}`,
                            { headers: { Authorization: `Bearer ${token}` } }
                        ),
                    ]);

                    const workloadDetails = {
                        preparers: workload.preparer || [],
                        recommenders: workload.recommenders || [],
                        pre_approver: workload.pre_approver,
                        approver: workload.approver,
                    };

                    allWorkloadsData.push({
                        courseLoadData: courseRes.data || [],
                        adminLoadData: adminRes.data || [],
                        repLoadData: repRes.data || [],
                        workLoadData: {
                            ...workload,
                            semester: this.$store.state.semester_schoolyear.semester,
                            schoolyear: this.$store.state.semester_schoolyear.schoolyear,
                        },
                        workloadDetails,
                    });
                }

                await exportAllApprovedPDF(allWorkloadsData);
            } catch (error) {
                if (error?.response?.status === 401) {
                    localStorage.removeItem('vuex');
                    localStorage.removeItem('aums-token');
                    window.location.reload();
                } else {
                    this.errorMsg = 'Failed to generate PDF.';
                    this.errorDetails = [error?.message || 'Unknown error while building the PDF.'];
                    console.error(error);
                }
            } finally {
                this.generating = false;
            }
        },
    },
});
</script>
